import Accordions from "@/common/components/Accordions";
import Box from "@/common/components/Box";
import MaintenanceObjectiveNew from "@/common/components/MaintenanceObjectiveNew";
import { FileEditIcon } from "lucide-react";
import { useMemo } from "react";
import { useMaintenanceEstablishmentChoiceAuctionEntryProposal } from "../../../../hooks/useMaintenanceEstablishmentChoiceAuctionEntryProposal";
import { MaintenanceEstablishmentChoiceAuctionEntryData } from "../../../../types/auctionEntry.type";
import { MaintenanceEstablishmentChoiceAuctionEntryUpdatedSummaryProposalData } from "../../../../types/updatedSummary.type";
import AuctionProposalDetailsExecutionObjectiveActivities from "./AuctionProposalDetailsExecutionObjectiveActivites";
import AuctionProposalDetailsExecutionObjectivePieces from "./AuctionProposalDetailsExecutionObjectivePieces";

interface AuctionProposalDetailsProps {
  proposal: MaintenanceEstablishmentChoiceAuctionEntryUpdatedSummaryProposalData;
  auctionEntry: MaintenanceEstablishmentChoiceAuctionEntryData | undefined;
  auctionEntryUpdatedAt: number;
}

const AuctionProposalDetails = ({
  proposal,
  auctionEntry,
  auctionEntryUpdatedAt,
}: AuctionProposalDetailsProps) => {
  const {
    data: proposalDetails,
    dataUpdatedAt: proposalDetailsUpdatedAt,
  } = useMaintenanceEstablishmentChoiceAuctionEntryProposal({
    auctionEntryProposalId: proposal.id,
  });

  const objectives = useMemo(() => {
    const objectives = auctionEntry?.forwarding?.directive_entry?.objectives

    objectives?.sort((a, b) => a.id.localeCompare(b.id))

    return objectives
  }, [auctionEntryUpdatedAt]);

  const objectiveOffsets = useMemo(() => {
    let pieceOffset = 0
    let activityOffset = 0

    return objectives?.map((objective) => {
      const currentPieceOffset = pieceOffset
      const currentActivityOffset = activityOffset

      pieceOffset += objective.execution_pieces?.length || 0
      activityOffset += objective.execution_activities?.length || 0

      return {
        pieceStartIndex: currentPieceOffset,
        activityStartIndex: currentActivityOffset,
      }
    })
  }, [objectives]);

  return (
    <div className="p-2 pt-0 flex flex-col gap-3">
      {proposalDetails?.disqualified_reason && (
        <Box className="p-2 flex items-center gap-2 bg-danger-op">
          <FileEditIcon size={20} strokeWidth={1.5} />

          <p className="text-xs">
            {proposalDetails?.disqualified_reason}
          </p>
        </Box>
      )}

      <Accordions
        defaultFirstItemOpened
        className="p-0"
        triggerClassName="p-3"
        contentClassName="px-3 pt-0"
        items={objectives?.map((objective, index) => ({
          id: objective.id,
          index,
          head: (
            <MaintenanceObjectiveNew
              systemDisplayName={objective.system.display_name}
              actionDisplayName={objective.action.display_name}
              partDisplayName={objective.part.display_name}
              actionDescription={objective.action.description}
            />
          ),
          body: (
            <div className="flex flex-col gap-4">
              <AuctionProposalDetailsExecutionObjectivePieces
                executionObjective={objective}
                proposalDetails={proposalDetails}
                pieceStartIndex={objectiveOffsets?.[index]?.pieceStartIndex || 0}
                auctionEntryUpdatedAt={auctionEntryUpdatedAt}
                proposalDetailsUpdatedAt={proposalDetailsUpdatedAt}
              />

              <AuctionProposalDetailsExecutionObjectiveActivities
                executionObjective={objective}
                proposalDetails={proposalDetails}
                activityStartIndex={objectiveOffsets?.[index]?.activityStartIndex || 0}
                auctionEntryUpdatedAt={auctionEntryUpdatedAt}
                proposalDetailsUpdatedAt={proposalDetailsUpdatedAt}
              />
            </div>
          ),
        }))}
      />
    </div>
  );
}

export default AuctionProposalDetails;
